import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdAddPhotoAlternate,
  MdCategory,
  MdCheck,
  MdClose,
  MdDelete,
  MdEdit,
  MdImage,
  MdMoreHoriz,
  MdOutlineImageNotSupported,
  MdUpload,
} from "react-icons/md";
import ColorPicker from "src/components/ColorPicker/ColorPicker";
import Icon from "src/components/Icon/Icon";
import IconSelector from "src/components/IconSelector/IconSelector";
import Modal from "src/components/Modal/Modal";
import ModalHasUnsavedChanges from "src/components/Modal/ModalHasUnsavedChanges";
import TextAreaInput from "src/components/TextAreaInput/TextAreaInput";
import TextInput from "src/components/TextInput/TextInput";
import { CONTACT_DEFAULT_ICONS } from "src/helpers/constants";
import {
  getMimeTypes,
  objectHasChanges,
  rgbaObjectToString,
  showNotification,
} from "src/helpers/utils";
import useModalsStore from "src/store/modalsStore";
import { EContactType, IContactDetailsData } from "./AddContactDetailsModal";
import useSourcesStore from "src/store/sourcesStore";
import useCache from "src/hooks/useCache";
import { filter, merge } from "lodash";
import SmartImage from "src/components/SmartImage/SmartImage";
import NoTableData from "src/components/NoTableData/NoTableData";
import ButtonGroup from "src/components/ButtonGroup/ButtonGroup";
import LinksList from "src/components/LinksList/LinksList";
import ContactCards from "./ContactCards";
import AddContactDetailsModal from "./AddContactDetailsModal";
import ConfirmDialog from "src/components/ConfirmDialog/ConfirmDialog";
import useMobile from "src/hooks/useMobile";
import { FILE_TYPES_IMAGES } from "src/constants/file-types";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "src/components/Button/Button.types";
import CheckBox from "src/components/CheckBox/CheckBox";

export interface IEditClientProviderModalPayload {
  name: string;
  icon: string;
  color: any;
  background: any;
  image: any;
  address: string;
  contact: any[];
  favorite: boolean;
}

enum EClientProviderTab {
  GENERAL = 1,
  CONTACT = 2,
}

const EditClientProviderModal: React.FC<IEditClientProviderModalPayload> = ({
  name,
  icon,
  color,
  background,
  image,
  address,
  contact = [],
  favorite = false,
}) => {
  const { isMobile } = useMobile();
  const { clearCache } = useCache();
  const { addSource } = useSourcesStore();
  const { t } = useTranslation();
  const [clientProviderName, setClientProviderName] = useState<string>(name);
  const [textColor, setTextColor] = useState<any>(color);
  const [bgColor, setBgColor] = useState<any>(background);
  const [selectedIcon, setSelectedIcon] = useState<string>(icon);
  const [enteredAddress, setEnteredAddress] = useState<string>(address);
  const [newImageToUpload, setNewImageToUpload] = useState<any>(null);
  const [isFavorite, setIsFavorite] = useState<boolean>(favorite);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentImage, setCurrentImage] = useState<any>(image);
  const { setShowEditClientProviderModal } = useModalsStore();
  const [savedContactData, setSavedContactData] = useState<any[]>(contact);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [activeTab, setActiveTab] = useState<EClientProviderTab>(
    EClientProviderTab.GENERAL,
  );
  const [showAddContactDetailsModal, setShowAddContactDetailsModal] =
    useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [indexToEdit, setIndexToEdit] = useState<number | null>(null);
  const [indexToDelete, setIndexToDelete] = useState<number | null>(null);
  const [isDeletingContactData, setIsDeletingContactData] =
    useState<boolean>(false);
  const [isEditingContactData, setIsEditingContactData] =
    useState<boolean>(false);

  const hasContactsSupport =
    "contacts" in navigator && "ContactsManager" in window;

  const resetFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const openFileDialog = useCallback(() => {
    resetFileInput();
    fileInputRef.current?.click();
  }, [resetFileInput]);

  const closeModal = () => {
    setShowEditClientProviderModal(false, null);
  };

  const importContact = useCallback(async () => {
    if (hasContactsSupport) {
      const props = await (navigator as any)?.contacts?.getProperties?.();

      const [contact] = await (navigator as any)?.contacts?.select(props, {
        multiple: false,
      });

      if (contact) {
        const { email = [], tel = [] } = contact;

        if (email.length) {
          setSavedContactData((prev: any) => [
            ...prev,
            {
              type: EContactType.EMAIL,
              value: email[0],
              name: "Correo electrónico",
              icon: CONTACT_DEFAULT_ICONS[EContactType.EMAIL],
            },
          ]);
        }

        if (tel.length) {
          setSavedContactData((prev: any) => [
            ...prev,
            {
              type: EContactType.PHONE,
              value: tel[0],
              name: "Teléfono",
              icon: CONTACT_DEFAULT_ICONS[EContactType.PHONE],
            },
          ]);
        }
      }
    }
  }, [hasContactsSupport]);

  const removeSelectedImage = useCallback(() => {
    setCurrentImage(null);
    setNewImageToUpload(null);
    setPreviewUrl("");
    resetFileInput();
  }, [resetFileInput]);

  useEffect(() => {
    if (!newImageToUpload) {
      setPreviewUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(newImageToUpload);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [newImageToUpload]);

  const hasChanges = useMemo(() => {
    return objectHasChanges({
      original: {
        icon,
        name,
        color,
        bg: background,
        picture: image,
        newImage: null,
        address,
        favorite,
      },
      edited: {
        icon: selectedIcon,
        name: clientProviderName,
        color: textColor,
        bg: bgColor,
        picture: currentImage,
        newImage: newImageToUpload,
        address: enteredAddress,
        favorite: isFavorite,
      },
    });
  }, [
    icon,
    name,
    color,
    background,
    image,
    address,
    selectedIcon,
    clientProviderName,
    textColor,
    bgColor,
    currentImage,
    newImageToUpload,
    enteredAddress,
    favorite,
    isFavorite,
  ]);

  const hasFieldsComplete = useMemo(() => {
    return !!(
      clientProviderName.trim() &&
      selectedIcon &&
      textColor &&
      bgColor
    );
  }, [clientProviderName, selectedIcon, textColor, bgColor]);

  const updateClientProvider = useCallback(
    async (
      fieldsToUpdate: any,
      { isProfileUpdate = false, isContactUpdate = false },
    ) => {
      setIsUpdating(true);

      await addSource({
        params: fieldsToUpdate,
      })
        .then(async () => {
          await clearCache();
          showNotification({
            message: t("Cambios guardados"),
            kind: "success",
          });
          setIsUpdating(false);
          if (isProfileUpdate) {
            closeModal();
          }
          if (isContactUpdate) {
            setShowAddContactDetailsModal(false);
            setShowEditClientProviderModal(true, {
              name,
              background: bgColor,
              color: textColor,
              icon: selectedIcon,
              image: newImageToUpload || currentImage || null,
              address: enteredAddress,
              contact: fieldsToUpdate.settings.contact,
              favorite: isFavorite,
            });
          }
        })
        .catch(() => {
          showNotification({
            message: t("Error guardando los cambios"),
            kind: "error",
          });
          setIsUpdating(false);
        });
    },
    [],
  );

  const deleteContactData = async () => {
    setIsDeletingContactData(true);

    const result = filter(contact, (_, i) => i !== indexToDelete);

    await addSource({
      params: {
        source: name,
        settings: merge(
          {},
          {
            contact: result,
          },
        ),
        originalSource: name,
      },
    })
      .then(async () => {
        await clearCache();
        showNotification({
          message: t("Datos de contacto eliminados"),
          kind: "success",
        });
        setIsDeletingContactData(false);
        setIndexToDelete(null);

        setShowEditClientProviderModal(true, {
          name,
          background: bgColor,
          color: textColor,
          icon: selectedIcon,
          image: newImageToUpload || currentImage || null,
          address: enteredAddress,
          contact: result,
          favorite: isFavorite,
        });
      })
      .catch(() => {
        showNotification({
          message: t("Error eliminando los datos de contacto"),
          kind: "error",
        });
        setIsDeletingContactData(false);
      });
  };

  const indexToEditData = () => {
    if (indexToEdit !== null) {
      return contact[indexToEdit];
    }

    return {
      type: EContactType.PHONE,
      value: "",
      name: "",
      icon: "",
    };
  };

  const editContactData = async (data: IContactDetailsData) => {
    setIsEditingContactData(true);

    if (indexToEdit !== null) {
      const result = contact;
      result[indexToEdit] = data;

      await addSource({
        params: {
          source: name,
          settings: merge(
            {},
            {
              contact: result,
            },
          ),
          originalSource: name,
        },
      })
        .then(async () => {
          await clearCache();
          showNotification({
            message: t("Datos de contacto actualizados"),
            kind: "success",
          });
          setIsEditingContactData(false);
          setIndexToEdit(null);

          setShowEditClientProviderModal(true, {
            name,
            background: bgColor,
            color: textColor,
            icon: selectedIcon,
            image: newImageToUpload || currentImage || null,
            address: enteredAddress,
            contact: result,
            favorite: isFavorite,
          });
        })
        .catch(() => {
          showNotification({
            message: t("Error actualizando los datos de contacto"),
            kind: "error",
          });
          setIsEditingContactData(false);
        });
    }
  };

  return (
    <>
      <Modal
        onEscape={() => closeModal()}
        className={clsx(["md:!max-w-[630px]", "md:!w-full"])}
      >
        <Modal.Header onClose={() => closeModal()} title="Editar" />

        <Modal.Body>
          <div
            className={clsx([
              "flex",
              "w-full",
              "flex-col",
              "gap-4",
              "md:gap-6",
            ])}
          >
            <input
              type="file"
              multiple={false}
              accept={getMimeTypes(FILE_TYPES_IMAGES)}
              ref={fileInputRef}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e?.target?.files?.[0] || null;

                if (!file) {
                  return;
                }

                setNewImageToUpload(file);
                setCurrentImage(null);

                // Permite volver a elegir el mismo archivo luego
                e.target.value = "";
              }}
              className={clsx(["hidden"])}
            />

            <LinksList
              links={[
                {
                  text: t("General"),
                  onClick: () => setActiveTab(EClientProviderTab.GENERAL),
                  active: activeTab === EClientProviderTab.GENERAL,
                },
                {
                  text: t("Contacto"),
                  onClick: () => setActiveTab(EClientProviderTab.CONTACT),
                  active: activeTab === EClientProviderTab.CONTACT,
                  count: contact.length,
                  showCount: true,
                },
              ]}
            />

            {activeTab === EClientProviderTab.GENERAL && (
              <>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-2",
                    "w-full",
                    "gap-2",
                    "bg-white dark:bg-transparent",
                    "py-0 md:p-0",
                  ])}
                >
                  <div
                    className={clsx([
                      "flex",
                      "rounded",
                      "overflow-hidden",
                      "h-full",
                    ])}
                  >
                    <div
                      className={clsx([
                        "flex",
                        "w-full",
                        "h-full",
                        "flex-col",
                        "items-center",
                        "justify-center",
                        "p-4",
                        "md:py-10 md:p-6",
                        "rounded",
                        "transition-all",
                        "gap-1",
                        "shadow",
                        "!max-h-[120px]",
                        "!min-h-[120px]",
                        "md:!max-h-[240px]",
                        "md:!min-h-[240px]",
                      ])}
                      style={{
                        backgroundColor: rgbaObjectToString(bgColor),
                        color: rgbaObjectToString(textColor),
                      }}
                    >
                      {!selectedIcon ? (
                        <MdCategory size={ICON_SIZES.XL * 2} />
                      ) : (
                        <Icon
                          name={selectedIcon as any}
                          size={ICON_SIZES.XL * 2}
                        />
                      )}

                      <div
                        className={clsx([
                          "text-center",
                          "capitalize",
                          "font-medium",
                          "text-sm",
                          "md:text-sm",
                          "p-2",
                          "truncate",
                          "max-w-full",
                        ])}
                      >
                        {clientProviderName}
                      </div>
                    </div>
                  </div>

                  <div
                    onClick={() => openFileDialog()}
                    className={clsx([
                      "flex",
                      "rounded",
                      "cursor-pointer",
                      "h-full",
                      "overflow-hidden",
                      "relative",
                    ])}
                  >
                    {newImageToUpload ? (
                      <SmartImage
                        src={previewUrl}
                        className={clsx([
                          "flex",
                          "h-full",
                          "w-full",
                          "object-cover",
                          "rounded",
                          "bg-checkers",
                          "!max-h-[120px]",
                          "!min-h-[120px]",
                          "md:!max-h-[240px]",
                          "md:!min-h-[240px]",
                        ])}
                      />
                    ) : currentImage ? (
                      <SmartImage
                        src={currentImage?.thumbnailUrl}
                        alt={currentImage?.originalName}
                        className={clsx([
                          "flex",
                          "h-full",
                          "w-full",
                          "object-cover",
                          "rounded",
                          "bg-checkers",
                          "!max-h-[120px]",
                          "!min-h-[120px]",
                          "md:!max-h-[240px]",
                          "md:!min-h-[240px]",
                        ])}
                      />
                    ) : (
                      <div
                        className={clsx([
                          "flex",
                          "h-full",
                          "w-full",
                          "items-center",
                          "justify-center",
                          "text-gray-500",
                          "bg-checkers",
                          "flex-col",
                          "!max-h-[120px]",
                          "!min-h-[120px]",
                          "md:!max-h-[240px]",
                          "md:!min-h-[240px]",
                        ])}
                      >
                        <NoTableData
                          animate={false}
                          className={clsx(["!my-0", "h-full"])}
                          icon={<MdImage />}
                          text="Sin imagen personalizada"
                        />
                      </div>
                    )}

                    <div
                      className={clsx([
                        "absolute",
                        "md:top-auto",
                        "md:left-auto",
                        "md:translate-x-0",
                        "md:translate-y-0",
                        "md:bottom-1",
                        "md:right-1",
                        "md:bg-white",
                        "bg-white/50",
                        "rounded",
                        "top-1/2",
                        "left-1/2",
                        "-translate-x-1/2",
                        "-translate-y-1/2",
                        "flex",
                        {
                          hidden: !currentImage && !newImageToUpload,
                        },
                      ])}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ButtonGroup
                        buttons={[
                          {
                            onClick: () => openFileDialog(),
                            icon: <MdAddPhotoAlternate />,
                            kind: EButtonKind.GHOST,
                          },
                          {
                            icon: <MdOutlineImageNotSupported />,
                            onClick: () => removeSelectedImage(),
                            kind: EButtonKind.GHOST,
                            hide: !newImageToUpload && !currentImage,
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-2",
                  ])}
                >
                  <ColorPicker
                    textColor={textColor}
                    onChange={(color: any) => {
                      setBgColor(color.rgb);
                    }}
                    color={bgColor}
                    label={t(
                      "pages:category.collapsibles.colors.bg_color_label",
                    )}
                    showText={false}
                  />

                  <ColorPicker
                    showText={false}
                    textColor={bgColor}
                    onChange={(color: any) => {
                      setTextColor(color.rgb);
                    }}
                    color={textColor}
                    label={t(
                      "pages:category.collapsibles.colors.text_color_label",
                    )}
                  />
                </div>

                <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
                  <TextInput
                    placeholder={t("Digita el nombre del Origen / Destino")}
                    onChange={(e: any) =>
                      setClientProviderName(e?.target?.value)
                    }
                    value={clientProviderName}
                    label={t("Nombre")}
                    required
                  />

                  <CheckBox
                    checked={isFavorite}
                    onChange={(isFavorite: boolean) =>
                      setIsFavorite(isFavorite)
                    }
                    label="Favorito"
                    description="Mostrar en la lista de contactos favoritos"
                  />

                  <TextAreaInput
                    value={enteredAddress}
                    onChange={(e: any) => setEnteredAddress(e?.target?.value)}
                    autoComplete="off"
                    label="Dirección física"
                    rows={3}
                    placeholder="Dirección física del Origen / Destino"
                  />
                </div>

                <IconSelector
                  onClick={(icon) => setSelectedIcon(icon)}
                  background={rgbaObjectToString(bgColor)}
                  selected={selectedIcon}
                  color={rgbaObjectToString(textColor) || ""}
                />
              </>
            )}

            {activeTab === EClientProviderTab.CONTACT && (
              <>
                {contact?.length ? (
                  <div className={clsx(["flex", "flex-col", "gap-4"])}>
                    <ContactCards
                      showCopy
                      data={contact}
                      customPageSize={isMobile ? 3 : 10}
                      actions={[
                        {
                          tooltip: t("common:tooltips.more_options"),
                          icon: <MdMoreHoriz />,
                          kind: EButtonKind.NORMAL,
                          onClick: (e) => {
                            e?.stopPropagation?.();
                            e?.preventDefault?.();
                          },
                          options: [
                            {
                              icon: <MdDelete size={ICON_SIZES.SM} />,
                              text: t("common:buttons.delete"),
                              kind: EButtonKind.DANGER,
                              onClick: (index: number) => {
                                setIndexToDelete(index);
                              },
                              disabled: isDeletingContactData,
                            },
                            {
                              icon: <MdEdit size={ICON_SIZES.SM} />,
                              text: t("common:buttons.edit"),
                              kind: EButtonKind.EDIT,
                              onClick: (index: number) => {
                                setIndexToEdit(index);
                              },
                              disabled: isEditingContactData,
                            },
                          ],
                        },
                      ]}
                      columns={2}
                    />
                  </div>
                ) : (
                  <NoTableData
                    text="Sin información de contacto"
                    className={clsx(["my-0"])}
                  />
                )}
              </>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer
          leftContent={
            activeTab === EClientProviderTab.GENERAL ? (
              hasChanges ? (
                <ModalHasUnsavedChanges />
              ) : null
            ) : (
              <ButtonGroup
                buttons={[
                  {
                    tooltip: t("common:buttons.add"),
                    icon: <MdAdd />,
                    onClick: () => setShowAddContactDetailsModal(true),
                  },
                  {
                    text: "Importar",
                    icon: <MdUpload />,
                    onClick: () => importContact(),
                    hide: !hasContactsSupport,
                  },
                ]}
              />
            )
          }
          actions={[
            {
              text: t("common:buttons.update"),
              onClick: async () => {
                await updateClientProvider(
                  {
                    source: clientProviderName,
                    settings: merge(
                      {},
                      {
                        color: textColor,
                        bg: bgColor,
                        icon: selectedIcon,
                        address: enteredAddress,
                        picture: currentImage,
                        favorite: isFavorite,
                      },
                    ),
                    originalSource: name,
                    file: newImageToUpload,
                  },
                  {
                    isProfileUpdate: true,
                  },
                );
              },
              icon: <MdCheck size={ICON_SIZES.SM} />,
              kind: EButtonKind.EDIT,
              isLoading: isUpdating,
              disabled: hasFieldsComplete ? !hasChanges || isUpdating : true,
              hide: activeTab === EClientProviderTab.CONTACT,
            },
            {
              text: t("common:buttons.close"),
              onClick: () => closeModal(),
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.SM} />,
              disabled: isUpdating,
            },
          ]}
        />
      </Modal>

      {showAddContactDetailsModal && (
        <AddContactDetailsModal
          onClose={() => {
            if (!isUpdating) {
              setShowAddContactDetailsModal(false);
            }
          }}
          onConfirm={async (data: any) => {
            await updateClientProvider(
              {
                source: name,
                settings: merge(
                  {},
                  {
                    contact: [...contact, data],
                  },
                ),
                originalSource: name,
              },
              {
                isContactUpdate: true,
              },
            );
          }}
          isLoading={isUpdating}
        />
      )}
      {indexToDelete !== null && (
        <ConfirmDialog
          onClose={() => {
            if (!isDeletingContactData) {
              setIndexToDelete(null);
            }
          }}
          description="¿Deseas eliminar la información de contacto?"
          title={t("dialogs:titles.update")}
          doubleConfirmRequired
          onConfirm={async () => {
            await deleteContactData();
          }}
          confirmKind={EButtonKind.DANGER}
          isLoading={isDeletingContactData}
        />
      )}
      {indexToEdit !== null && (
        <AddContactDetailsModal
          onClose={() => {
            if (!isEditingContactData) {
              setIndexToEdit(null);
            }
          }}
          isEditMode
          dataToEdit={indexToEditData()}
          onConfirm={async (data: IContactDetailsData) => {
            await editContactData(data);
          }}
          isLoading={isEditingContactData}
        />
      )}
    </>
  );
};

export default EditClientProviderModal;
